import { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { toast } from 'sonner'
import { requestService } from '../services/requestService'
import { employeeService } from '../services/employeeService'
import { requestTypeBlockService, BlockedTypesMap } from '../services/requestTypeBlockService'
import { Employee, REQUEST_TYPE_LABELS, ValidationResult, RequestType, SHIFT_LABELS } from '../types'
import { AlertCircle, CheckCircle, ArrowLeft, FileText, Search, ChevronDown, X, Upload, Paperclip, Info, Ban, Calendar } from 'lucide-react'
import { DatePicker } from '../components/ui/DatePicker'
import { useAuth } from '../contexts/AuthContext'
import { extractApiErrors, ValidationError } from '../utils/errorHandler'

const schema = z.object({
  employeeId: z.string().min(1, 'Empleado requerido'),
  requestType: z.string().min(1, 'Tipo de solicitud requerido'),
  startDate: z.string().min(1, 'Fecha de inicio requerida'),
  endDate: z.string().min(1, 'Fecha de fin requerida'),
  secondDate: z.string().optional(), // Segunda fecha para DLE con días no consecutivos
  compensationType: z.string().optional(), // Solo para DLE: RECUPERABLE o DESCUENTO_NOMINA
  notes: z.string().optional(),
})

type FormData = z.infer<typeof schema>

// Tipo de compensación para DLE
type CompensationType = 'RECUPERABLE' | 'DESCUENTO_NOMINA'

// Fechas de arrancada (16, 17, 18 de septiembre)
const isDateInArrancada = (dateStr: string): boolean => {
  if (!dateStr) return false
  const date = new Date(dateStr)
  const month = date.getMonth() // 0-indexed, septiembre = 8
  const day = date.getDate()
  return month === 8 && day >= 16 && day <= 18
}

export function NewRequestPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const isAdmin = user?.role === 'ADMIN'
  const [employees, setEmployees] = useState<Employee[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [validation, setValidation] = useState<ValidationResult | null>(null)
  const [error, setError] = useState('')
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([])
  const [requestsEnabled, setRequestsEnabled] = useState<boolean | null>(null)
  const [checkingStatus, setCheckingStatus] = useState(true)
  const [blockedTypes, setBlockedTypes] = useState<BlockedTypesMap>({})

  // Estados para el buscador de empleados
  const [searchTerm, setSearchTerm] = useState('')
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Estado para archivo adjunto (Vacaciones Fiestas Cáseda)
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Estado para DLE - tipo de compensación
  const [compensationType, setCompensationType] = useState<CompensationType | ''>('')

  // Estado para ECV - duración en meses
  const [ecvMonths, setEcvMonths] = useState<1 | 2 | -1 | null>(null)

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      employeeId: !isAdmin && user?.employeeId ? user.employeeId : '',
    },
  })

  const watchedFields = watch()

  const [isSearching, setIsSearching] = useState(false)

  // Función para recargar los tipos bloqueados
  const refreshBlockedTypes = async () => {
    if (isAdmin) return
    try {
      const blocked = await requestTypeBlockService.getActiveBlocks()
      setBlockedTypes(blocked)

      // Si el tipo seleccionado ahora está bloqueado, limpiar la selección
      const currentType = watchedFields.requestType
      if (currentType && blocked[currentType]?.blocked && !blocked[currentType].allowedDays) {
        setValue('requestType', '')
        toast.warning('Tipo de solicitud no disponible', {
          description: 'El tipo de solicitud que tenías seleccionado ya no está disponible.',
        })
      }
    } catch (error) {
      console.error('Error refreshing blocked types:', error)
    }
  }

  // Verificar si las solicitudes están habilitadas y cargar tipos bloqueados
  useEffect(() => {
    const checkRequestsStatus = async () => {
      // Si es admin, siempre puede crear solicitudes
      if (isAdmin) {
        setRequestsEnabled(true)
        setCheckingStatus(false)
        return
      }

      try {
        setCheckingStatus(true)
        const [status, blocked] = await Promise.all([
          requestService.getRequestsStatus(),
          requestTypeBlockService.getActiveBlocks(),
        ])
        setRequestsEnabled(status.requestsEnabled)
        setBlockedTypes(blocked)
      } catch (error) {
        console.error('Error checking requests status:', error)
        // En caso de error, asumimos que está habilitado para no bloquear innecesariamente
        setRequestsEnabled(true)
      } finally {
        setCheckingStatus(false)
      }
    }

    checkRequestsStatus()
  }, [isAdmin])

  // Refrescar tipos bloqueados cuando la ventana vuelve a estar activa
  useEffect(() => {
    if (isAdmin) return

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        refreshBlockedTypes()
      }
    }

    const handleFocus = () => {
      refreshBlockedTypes()
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('focus', handleFocus)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('focus', handleFocus)
    }
  }, [isAdmin, watchedFields.requestType])

  // Cargar empleados iniciales o buscar
  useEffect(() => {
    if (isAdmin) {
      const fetchEmployees = async () => {
        setIsSearching(true)
        try {
          const response = await employeeService.getAll({
            limit: 50,
            isActive: true,
            search: searchTerm.trim() || undefined
          })
          // Ordenar por employeeId
          const sorted = response.data.sort((a, b) =>
            a.employeeId.localeCompare(b.employeeId, undefined, { numeric: true })
          )
          setEmployees(sorted)
        } catch (error) {
          console.error('Error fetching employees:', error)
        } finally {
          setIsSearching(false)
        }
      }

      // Debounce de 300ms para la búsqueda
      const timeoutId = setTimeout(fetchEmployees, searchTerm ? 300 : 0)
      return () => clearTimeout(timeoutId)
    } else if (user?.employeeId) {
      setValue('employeeId', user.employeeId)
    }
  }, [isAdmin, user, setValue, searchTerm])

  // Cerrar dropdown al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Seleccionar empleado
  const handleSelectEmployee = (emp: Employee) => {
    setSelectedEmployee(emp)
    setValue('employeeId', emp.id)
    setSearchTerm('')
    setIsDropdownOpen(false)
  }

  // Limpiar selección
  const handleClearEmployee = () => {
    setSelectedEmployee(null)
    setValue('employeeId', '')
    setSearchTerm('')
  }

  // Verificar si es Vacaciones Fiestas Cáseda
  const isVacacionesCaseda = watchedFields.requestType === 'VACACIONES_FIESTAS_CASEDA'

  // Verificar si es Días de Libre Elección
  const isDiasLibreEleccion = watchedFields.requestType === 'DIAS_LIBRE_ELECCION'

  // Verificar si es Excedencia Corta
  const isExcedenciaCorta = watchedFields.requestType === 'EXCEDENCIA_CORTA_VACACIONES'

  // Verificar si alguna fecha está en periodo de arrancada
  const isStartDateInArrancada = isDateInArrancada(watchedFields.startDate)
  const isEndDateInArrancada = isDateInArrancada(watchedFields.endDate)
  const isSecondDateInArrancada = isDateInArrancada(watchedFields.secondDate || '')
  const anyDateInArrancada = isStartDateInArrancada || isEndDateInArrancada || isSecondDateInArrancada

  // Si está en arrancada, el tipo de compensación debe ser RECUPERABLE
  const forceRecuperable = isDiasLibreEleccion && anyDateInArrancada

  // El documento es obligatorio para empleados, opcional para admin
  const isDocumentRequired = isVacacionesCaseda && !isAdmin

  // Calcular fechas para Vacaciones Cáseda (7-13 septiembre)
  // Usar el año actual o el siguiente si ya pasó septiembre
  const getCasedaDates = () => {
    const now = new Date()
    let year = now.getFullYear()
    // Si ya pasó el 13 de septiembre, usar el próximo año
    if (now.getMonth() > 8 || (now.getMonth() === 8 && now.getDate() > 13)) {
      year = year + 1
    }
    return {
      minDate: new Date(year, 8, 7),  // 7 de septiembre
      maxDate: new Date(year, 8, 13), // 13 de septiembre
    }
  }
  const casedaDates = isVacacionesCaseda ? getCasedaDates() : null

  // Auto-establecer fechas cuando se selecciona Vacaciones Cáseda
  useEffect(() => {
    if (isVacacionesCaseda && casedaDates) {
      // Formatear fechas para el formulario (YYYY-MM-DD)
      const formatDate = (date: Date) => {
        const year = date.getFullYear()
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const day = date.getDate().toString().padStart(2, '0')
        return `${year}-${month}-${day}`
      }
      // Establecer fechas por defecto (7 y 13 de septiembre)
      setValue('startDate', formatDate(casedaDates.minDate))
      setValue('endDate', formatDate(casedaDates.maxDate))
      // Limpiar validación anterior
      setValidation(null)
    }
  }, [isVacacionesCaseda])

  // Resetear compensationType y secondDate cuando cambia el tipo de solicitud
  useEffect(() => {
    if (!isDiasLibreEleccion) {
      setCompensationType('')
      setValue('compensationType', '')
      setValue('secondDate', '')
    }
  }, [isDiasLibreEleccion, setValue])

  // Resetear ecvMonths cuando cambia el tipo de solicitud
  useEffect(() => {
    if (!isExcedenciaCorta) {
      setEcvMonths(null)
    }
  }, [isExcedenciaCorta])

  // Auto-calcular fechas de ECV cuando cambia startDate o ecvMonths
  useEffect(() => {
    if (!isExcedenciaCorta || !ecvMonths || !watchedFields.startDate) return
    // Si el admin eligió fecha personalizada (-1), no auto-calcular
    if (ecvMonths === -1) return

    const start = new Date(watchedFields.startDate)

    // Calcular endDate: fecha inicio + N meses - 1 día
    // Ej: 05/06 + 1 mes = 04/07, 05/06 + 2 meses = 04/08
    const endDate = new Date(start)
    endDate.setMonth(endDate.getMonth() + ecvMonths)
    endDate.setDate(endDate.getDate() - 1)
    const y = endDate.getFullYear()
    const m = (endDate.getMonth() + 1).toString().padStart(2, '0')
    const d = endDate.getDate().toString().padStart(2, '0')
    setValue('endDate', `${y}-${m}-${d}`)
    setValidation(null)
  }, [isExcedenciaCorta, ecvMonths, watchedFields.startDate, setValue])

  // Forzar RECUPERABLE cuando está en arrancada
  useEffect(() => {
    if (forceRecuperable && compensationType !== 'RECUPERABLE') {
      setCompensationType('RECUPERABLE')
      setValue('compensationType', 'RECUPERABLE')
    }
  }, [forceRecuperable, compensationType, setValue])

  // Ref para trackear el compensationType anterior y evitar borrar fechas innecesariamente
  const prevCompensationTypeRef = useRef<CompensationType | ''>('')

  // Manejar cambio de compensationType preservando fechas
  useEffect(() => {
    const prevType = prevCompensationTypeRef.current
    prevCompensationTypeRef.current = compensationType

    // Solo actuar si hay cambio real de tipo
    if (prevType === compensationType) return

    if (compensationType === 'DESCUENTO_NOMINA' && prevType !== 'DESCUENTO_NOMINA') {
      // Cambiando A descuento nómina: copiar endDate a secondDate si existe
      if (watchedFields.endDate && !watchedFields.secondDate) {
        setValue('secondDate', watchedFields.endDate)
      }
    } else if (compensationType === 'RECUPERABLE' && prevType === 'DESCUENTO_NOMINA') {
      // Cambiando DE descuento nómina A recuperable: endDate = startDate (por defecto 1 día)
      if (watchedFields.startDate) {
        setValue('endDate', watchedFields.startDate)
      }
      // Limpiar secondDate ya que no se usa en RECUPERABLE
      setValue('secondDate', '')
    } else if (compensationType !== 'DESCUENTO_NOMINA') {
      // Cualquier otro caso que no sea descuento nómina: limpiar secondDate
      setValue('secondDate', '')
    }
  }, [compensationType, setValue, watchedFields.endDate, watchedFields.secondDate])

  // Manejar selección de archivo
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      // Validar tamaño (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Archivo demasiado grande', {
          description: 'El archivo no puede superar los 5MB.',
        })
        return
      }
      // Validar tipo
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg']
      if (!allowedTypes.includes(file.type)) {
        toast.error('Tipo de archivo no permitido', {
          description: 'Solo se permiten archivos PDF, JPG o PNG.',
        })
        return
      }
      setAttachmentFile(file)
    }
  }

  // Limpiar archivo
  const handleClearFile = () => {
    setAttachmentFile(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleValidate = async () => {
    if (
      !watchedFields.employeeId ||
      !watchedFields.requestType ||
      !watchedFields.startDate ||
      !watchedFields.endDate
    ) {
      toast.error('Completa todos los campos', {
        description: 'Debes seleccionar empleado, tipo, fecha inicio y fin.',
      })
      return
    }

    // Verificar en tiempo real si el tipo sigue disponible (para empleados)
    if (!isAdmin) {
      try {
        const currentBlocked = await requestTypeBlockService.getActiveBlocks()
        setBlockedTypes(currentBlocked)
        const blockInfo = currentBlocked[watchedFields.requestType]
        if (blockInfo?.blocked && !blockInfo.allowedDays) {
          setValue('requestType', '')
          toast.error('Tipo de solicitud no disponible', {
            description: 'Este tipo de solicitud ha sido bloqueado. Por favor, selecciona otro.',
          })
          return
        }
      } catch (error) {
        console.error('Error checking blocked types:', error)
      }
    }

    // Validación específica para DLE
    if (isDiasLibreEleccion) {
      if (!compensationType) {
        toast.error('Tipo de compensación requerido', {
          description: 'Debes seleccionar si el día será recuperable o descuento en nómina.',
        })
        return
      }
      if (compensationType === 'DESCUENTO_NOMINA' && !watchedFields.secondDate && !isAdmin) {
        toast.error('Segundo día requerido', {
          description: 'Para descuento en nómina debes seleccionar dos días.',
        })
        return
      }
    }

    try {
      // Preparar datos de validación
      const validateData: Parameters<typeof requestService.validate>[0] = {
        employeeId: watchedFields.employeeId,
        requestType: watchedFields.requestType as RequestType,
        startDate: watchedFields.startDate,
        endDate: isDiasLibreEleccion && compensationType === 'DESCUENTO_NOMINA'
          ? watchedFields.startDate  // En descuento nómina, endDate = startDate (primer día)
          : watchedFields.endDate,
        // Campos específicos para DLE
        ...(isDiasLibreEleccion && {
          compensationType: compensationType as 'RECUPERABLE' | 'DESCUENTO_NOMINA',
          secondDate: compensationType === 'DESCUENTO_NOMINA' && watchedFields.secondDate ? watchedFields.secondDate : undefined,
        }),
      }

      // Si es Vacaciones Fiestas Cáseda y hay archivo, incluir en validación
      if (isVacacionesCaseda && attachmentFile) {
        validateData.attachmentName = attachmentFile.name
        // Convertir a base64 para la validación
        const reader = new FileReader()
        const base64Promise = new Promise<string>((resolve) => {
          reader.onloadend = () => resolve(reader.result as string)
          reader.readAsDataURL(attachmentFile)
        })
        validateData.attachmentUrl = await base64Promise
      }

      const result = await requestService.validate(validateData)
      setValidation(result)
      setAdminOverride(false)
      setError('')
      setValidationErrors([])

      // Mostrar toast con el resultado de la validación
      if (result.isValid) {
        toast.success('Solicitud válida', {
          description: 'Puedes proceder a crear la solicitud.',
        })
      } else {
        toast.error('Solicitud no válida', {
          description: result.errors.join('. '),
          duration: 8000,
        })
      }
    } catch (error: unknown) {
      const { message, details } = extractApiErrors(error)
      setError(message)
      setValidationErrors(details)

      const errorMessages = details.length > 0
        ? details.map(d => d.message).join('. ')
        : message

      toast.error(message, {
        description: errorMessages !== message ? errorMessages : undefined,
        duration: 6000,
      })
    }
  }

  const onSubmit = async (data: FormData) => {
    if (!isAdmin && !validation?.isValid) {
      toast.error('Validación requerida', {
        description: 'Debes validar la solicitud antes de crearla.',
      })
      return
    }

    // Verificar una última vez si el tipo sigue disponible antes de enviar
    if (!isAdmin) {
      try {
        const currentBlocked = await requestTypeBlockService.getActiveBlocks()
        setBlockedTypes(currentBlocked)
        const blockInfo = currentBlocked[data.requestType]
        if (blockInfo?.blocked && !blockInfo.allowedDays) {
          setValue('requestType', '')
          setValidation(null)
          toast.error('Tipo de solicitud no disponible', {
            description: 'Este tipo de solicitud ha sido bloqueado mientras completabas el formulario. Por favor, selecciona otro.',
          })
          return
        }
      } catch (error) {
        console.error('Error checking blocked types:', error)
      }
    }

    setIsLoading(true)
    setError('')
    setValidationErrors([])

    try {
      // Preparar datos con archivo adjunto si existe
      const requestData: Parameters<typeof requestService.create>[0] = {
        employeeId: data.employeeId,
        requestType: data.requestType as RequestType,
        startDate: data.startDate,
        endDate: isDiasLibreEleccion && compensationType === 'DESCUENTO_NOMINA'
          ? data.startDate  // En descuento nómina, endDate = startDate (primer día)
          : data.endDate,
        notes: data.notes,
        // Campos específicos para DLE
        ...(isDiasLibreEleccion && {
          compensationType: compensationType as 'RECUPERABLE' | 'DESCUENTO_NOMINA',
          secondDate: compensationType === 'DESCUENTO_NOMINA' && data.secondDate ? data.secondDate : undefined,
        }),
      }

      // Admin override: forzar aprobación aunque no cumpla reglas
      if (isAdmin && adminOverride && validation && !validation.isValid) {
        requestData.adminOverride = true
      }

      // Si es Vacaciones Fiestas Cáseda y hay archivo, añadir nombre
      // TODO: En producción, implementar subida real a S3 o similar
      if (isVacacionesCaseda && attachmentFile) {
        requestData.attachmentName = attachmentFile.name
        // Por ahora, crear una URL temporal con base64 para el archivo pequeño
        const reader = new FileReader()
        const base64Promise = new Promise<string>((resolve) => {
          reader.onloadend = () => resolve(reader.result as string)
          reader.readAsDataURL(attachmentFile)
        })
        requestData.attachmentUrl = await base64Promise
      }

      const result = await requestService.create(requestData)

      if (result.validation.isValid || result.validation.adminOverrideApplied) {
        toast.success('Solicitud creada', {
          description: result.validation.adminOverrideApplied
            ? 'Solicitud aprobada por override de administrador.'
            : 'Tu solicitud ha sido enviada correctamente.',
        })
        navigate('/requests')
      } else {
        setValidation(result.validation)
        toast.error('Solicitud no válida', {
          description: 'Revisa los errores de validación.',
        })
      }
    } catch (error: unknown) {
      const { message, details } = extractApiErrors(error)
      setError(message)
      setValidationErrors(details)

      // Mostrar toast con errores
      const errorMessages = details.length > 0
        ? details.map(d => d.message).join('. ')
        : message

      toast.error(message, {
        description: errorMessages !== message ? errorMessages : undefined,
        duration: 6000,
      })
    } finally {
      setIsLoading(false)
    }
  }

  // Estado para admin override
  const [adminOverride, setAdminOverride] = useState(false)

  // Admin puede enviar si la validación pasó O si activa el override
  // Empleados solo pueden enviar si la validación pasó
  const canSubmit = isAdmin
    ? (validation?.isValid === true || (validation !== null && adminOverride)) && (!isDocumentRequired || attachmentFile !== null)
    : validation?.isValid === true && (!isDocumentRequired || attachmentFile !== null)

  // Mostrar spinner mientras se verifica el estado
  if (checkingStatus) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600"></div>
      </div>
    )
  }

  // Mostrar mensaje de solicitudes deshabilitadas para empleados
  if (!isAdmin && requestsEnabled === false) {
    return (
      <div className="max-w-2xl mx-auto space-y-6 animate-slide-in">
        {/* Header */}
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/requests')}
            className="p-2.5 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-xl transition-colors"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
          </button>
          <div className="page-header mb-0">
            <h1 className="page-title flex items-center gap-3">
              <div className="p-2 rounded-xl bg-gradient-to-br from-gray-400 to-gray-600 shadow-sm">
                <FileText className="h-6 w-6 text-white" />
              </div>
              Nueva Solicitud
            </h1>
            <p className="page-subtitle">Crear una nueva solicitud de licencia</p>
          </div>
        </div>

        {/* Mensaje de solicitudes deshabilitadas */}
        <div className="card p-8 text-center">
          <div className="mx-auto w-16 h-16 bg-error-100 dark:bg-error-900/30 rounded-full flex items-center justify-center mb-4">
            <Ban className="h-8 w-8 text-error-600 dark:text-error-400" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            Solicitudes temporalmente deshabilitadas
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-md mx-auto">
            La creación de nuevas solicitudes está temporalmente deshabilitada por el departamento de RRHH.
            Por favor, contacta con RRHH si necesitas realizar una solicitud urgente.
          </p>
          <button
            onClick={() => navigate('/requests')}
            className="btn btn-secondary"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver a Solicitudes
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto space-y-4 sm:space-y-6 animate-slide-in">
      {/* Header */}
      <div className="flex items-center gap-3 sm:gap-4">
        <button
          onClick={() => navigate('/requests')}
          className="p-2 sm:p-2.5 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg sm:rounded-xl transition-colors flex-shrink-0"
        >
          <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-gray-300" />
        </button>
        <div className="page-header mb-0 min-w-0">
          <h1 className="page-title flex items-center gap-2 sm:gap-3 text-lg sm:text-2xl lg:text-3xl">
            <div className="p-1.5 sm:p-2 rounded-lg sm:rounded-xl bg-gradient-to-br from-primary-500 to-primary-700 shadow-sm flex-shrink-0">
              <FileText className="h-4 w-4 sm:h-6 sm:w-6 text-white" />
            </div>
            Nueva Solicitud
          </h1>
          <p className="page-subtitle text-sm sm:text-base">Crear una nueva solicitud de licencia</p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="card p-4 sm:p-6 space-y-4 sm:space-y-6">
        {(error || validationErrors.length > 0) && (
          <div className="bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-xl p-4 animate-slide-in">
            <div className="flex items-center gap-3 mb-2">
              <AlertCircle className="h-5 w-5 text-error-500 flex-shrink-0" />
              <p className="text-sm font-medium text-error-700 dark:text-error-400">{error}</p>
            </div>
            {validationErrors.length > 0 && (
              <ul className="ml-8 list-disc text-sm text-error-600 dark:text-error-400 space-y-1">
                {validationErrors.map((err, index) => (
                  <li key={index}>
                    <span className="font-medium">{err.field}:</span> {err.message}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Empleado */}
        <div>
          <label className="label">Empleado</label>
          {isAdmin ? (
            <div ref={dropdownRef} className="relative">
              {/* Campo de búsqueda o empleado seleccionado */}
              {selectedEmployee ? (
                <div className="flex items-center justify-between p-3 bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-xl">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-semibold text-sm">
                      {selectedEmployee.fullName.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">
                        {selectedEmployee.employeeId} - {selectedEmployee.fullName}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {selectedEmployee.position} • {SHIFT_LABELS[selectedEmployee.shift] || selectedEmployee.shift}
                      </p>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={handleClearEmployee}
                    className="p-1.5 hover:bg-primary-100 dark:hover:bg-primary-800 rounded-lg transition-colors"
                  >
                    <X className="h-4 w-4 text-primary-600 dark:text-primary-400" />
                  </button>
                </div>
              ) : (
                <div
                  className="relative cursor-pointer"
                  onClick={() => setIsDropdownOpen(true)}
                >
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value)
                      setIsDropdownOpen(true)
                    }}
                    onFocus={() => setIsDropdownOpen(true)}
                    placeholder="Buscar por ID, nombre, turno o puesto..."
                    className="input pl-10 pr-10 w-full"
                  />
                  <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                </div>
              )}

              {/* Dropdown con lista de empleados */}
              {isDropdownOpen && !selectedEmployee && (
                <div className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-lg max-h-64 overflow-y-auto">
                  {isSearching ? (
                    <div className="p-4 flex items-center justify-center gap-2 text-gray-500 dark:text-gray-400">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-500 border-t-transparent"></div>
                      Buscando...
                    </div>
                  ) : employees.length === 0 ? (
                    <div className="p-4 text-center text-gray-500 dark:text-gray-400">
                      {searchTerm ? 'No se encontraron empleados' : 'Escribe para buscar empleados'}
                    </div>
                  ) : (
                    employees.map((emp) => (
                      <div
                        key={emp.id}
                        onClick={() => handleSelectEmployee(emp)}
                        className="flex items-center gap-3 p-3 hover:bg-gray-50 dark:hover:bg-slate-700 cursor-pointer border-b border-gray-100 dark:border-slate-700 last:border-b-0"
                      >
                        <div className="h-8 w-8 rounded-full bg-gradient-to-br from-secondary-400 to-secondary-600 flex items-center justify-center text-white font-semibold text-xs flex-shrink-0">
                          {emp.fullName.charAt(0).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                            {emp.employeeId} - {emp.fullName}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {emp.position} • {SHIFT_LABELS[emp.shift] || emp.shift}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}

              {/* Input oculto para react-hook-form */}
              <input type="hidden" {...register('employeeId')} />
              {errors.employeeId && (
                <p className="text-sm text-error-500 mt-1">{errors.employeeId.message}</p>
              )}
            </div>
          ) : (
            <div className="flex items-center gap-3 p-4 bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-dark-border rounded-xl">
              <div className="h-12 w-12 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 flex items-center justify-center text-white font-semibold">
                {user?.name?.charAt(0).toUpperCase()}
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">{user?.name}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{user?.email}</p>
              </div>
              <input type="hidden" {...register('employeeId')} />
            </div>
          )}
        </div>

        {/* Tipo de Solicitud */}
        <div>
          <label className="label">Tipo de Solicitud</label>
          <select {...register('requestType')} className="input">
            <option value="">Seleccionar tipo...</option>
            {Object.entries(REQUEST_TYPE_LABELS)
              .filter(([value]) => {
                // Si es admin, mostrar todos los tipos
                if (isAdmin) return true
                // Si el tipo está bloqueado completamente (sin allowedDays), no mostrarlo
                const blockInfo = blockedTypes[value]
                if (blockInfo?.blocked && !blockInfo.allowedDays) {
                  return false
                }
                return true
              })
              .map(([value, label]) => {
                // Si tiene restricción de días, añadir nota
                const blockInfo = blockedTypes[value]
                const suffix = blockInfo?.blocked && blockInfo.allowedDays
                  ? ` (solo ${blockInfo.allowedDays} días)`
                  : ''
                return (
                  <option key={value} value={value}>
                    {label}{suffix}
                  </option>
                )
              })}
          </select>
          {errors.requestType && (
            <p className="text-sm text-error-500 mt-1">{errors.requestType.message}</p>
          )}
        </div>

        {/* Fechas */}
        {isExcedenciaCorta ? (
          <div className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Info className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  La excedencia corta debe ser de exactamente 1 o 2 meses. Selecciona la fecha de inicio y la duración.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
              <div>
                <label className="label">Fecha de inicio</label>
                <DatePicker
                  value={watchedFields.startDate}
                  onChange={(date) => setValue('startDate', date)}
                  placeholder="DD/MM/AAAA"
                  error={!!errors.startDate}
                  minDate={isAdmin ? new Date(new Date().getFullYear(), 0, 1) : new Date()}
                  maxDate={new Date(new Date().getFullYear(), 11, 31)}
                />
                {errors.startDate && (
                  <p className="text-sm text-error-500 mt-1">{errors.startDate.message}</p>
                )}
              </div>
              <div>
                <label className="label">Duración</label>
                <div className={`grid ${isAdmin ? 'grid-cols-3' : 'grid-cols-2'} gap-3`}>
                  <button
                    type="button"
                    onClick={() => setEcvMonths(1)}
                    className={`p-3 rounded-xl border-2 transition-all text-center ${
                      ecvMonths === 1
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <span className="font-medium">1 mes</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => setEcvMonths(2)}
                    className={`p-3 rounded-xl border-2 transition-all text-center ${
                      ecvMonths === 2
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                  >
                    <span className="font-medium">2 meses</span>
                  </button>
                  {isAdmin && (
                    <button
                      type="button"
                      onClick={() => setEcvMonths(-1)}
                      className={`p-3 rounded-xl border-2 transition-all text-center ${
                        ecvMonths === -1
                          ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <span className="font-medium text-sm">Otra</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
            {isAdmin && ecvMonths === -1 && (
              <div>
                <label className="label">Fecha de fin (personalizada)</label>
                <DatePicker
                  value={watchedFields.endDate}
                  onChange={(date) => {
                    setValue('endDate', date)
                    setValidation(null)
                  }}
                  placeholder="DD/MM/AAAA"
                  error={!!errors.endDate}
                  minDate={watchedFields.startDate ? new Date(watchedFields.startDate) : new Date()}
                  maxDate={new Date(new Date().getFullYear() + 1, 11, 31)}
                />
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                  Al no ser meses exactos, deberá activar la opción de forzar aprobación.
                </p>
              </div>
            )}
            {watchedFields.startDate && ecvMonths && ecvMonths > 0 && watchedFields.endDate && (
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Periodo: <span className="font-medium">{new Date(watchedFields.startDate).toLocaleDateString('es-ES')}</span> al <span className="font-medium">{new Date(watchedFields.endDate).toLocaleDateString('es-ES')}</span>
              </p>
            )}
          </div>
        ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          <div>
            <label className="label">
              {isDiasLibreEleccion && compensationType === 'DESCUENTO_NOMINA' ? 'Primer día' : 'Fecha de Inicio'}
            </label>
            <DatePicker
              value={watchedFields.startDate}
              onChange={(date) => {
                setValue('startDate', date)
                // Para DLE RECUPERABLE: auto-ajustar endDate = startDate para evitar que quede un rango de 2+ días
                if (isDiasLibreEleccion && compensationType === 'RECUPERABLE') {
                  setValue('endDate', date)
                }
              }}
              placeholder="DD/MM/AAAA"
              error={!!errors.startDate}
              minDate={casedaDates?.minDate || (isAdmin ? new Date(new Date().getFullYear(), 0, 1) : new Date())}
              maxDate={casedaDates?.maxDate || new Date(new Date().getFullYear() + 4, 11, 31)}
            />
            {errors.startDate && (
              <p className="text-sm text-error-500 mt-1">{errors.startDate.message}</p>
            )}
            {isVacacionesCaseda && casedaDates && (
              <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                Solo disponible del 7 al 13 de septiembre de {casedaDates.minDate.getFullYear()}
              </p>
            )}
          </div>
          <div>
            <label className="label">
              {isDiasLibreEleccion && compensationType === 'DESCUENTO_NOMINA'
                ? (isAdmin ? 'Segundo día (opcional)' : 'Segundo día')
                : 'Fecha de Fin'}
            </label>
            <DatePicker
              value={isDiasLibreEleccion && compensationType === 'DESCUENTO_NOMINA'
                ? watchedFields.secondDate || ''
                : watchedFields.endDate}
              onChange={(date) => {
                if (isDiasLibreEleccion && compensationType === 'DESCUENTO_NOMINA') {
                  setValue('secondDate', date)
                  // En descuento nómina, startDate y endDate son el mismo (primer día)
                  setValue('endDate', watchedFields.startDate)
                } else {
                  setValue('endDate', date)
                }
              }}
              placeholder="DD/MM/AAAA"
              minDate={casedaDates?.minDate || (watchedFields.startDate ? new Date(watchedFields.startDate) : (isAdmin ? new Date(new Date().getFullYear(), 0, 1) : new Date()))}
              maxDate={casedaDates?.maxDate || new Date(new Date().getFullYear() + 4, 11, 31)}
              error={!!errors.endDate}
            />
            {errors.endDate && (
              <p className="text-sm text-error-500 mt-1">{errors.endDate.message}</p>
            )}
          </div>
        </div>
        )}

        {/* Indicador de días para DLE */}
        {isDiasLibreEleccion && watchedFields.startDate && watchedFields.endDate && (
          (() => {
            const s = new Date(watchedFields.startDate)
            const e = new Date(watchedFields.endDate)
            s.setHours(0, 0, 0, 0)
            e.setHours(0, 0, 0, 0)
            let days: number
            if (compensationType === 'DESCUENTO_NOMINA') {
              // Para DESCUENTO_NOMINA: contar días únicos (startDate + secondDate si es diferente)
              const secondDate = watchedFields.secondDate
              if (secondDate && secondDate !== watchedFields.startDate) {
                days = 2
              } else {
                days = 1
              }
            } else {
              days = Math.round(Math.abs((e.getTime() - s.getTime()) / (24 * 60 * 60 * 1000))) + 1
            }
            return (
              <div className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium ${
                days === 1
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                  : 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300'
              }`}>
                <Calendar className="h-4 w-4" />
                Se solicitará {days} {days === 1 ? 'día' : 'días'} de libre elección
              </div>
            )
          })()
        )}

        {/* Días de Libre Elección - Tipo de Compensación */}
        {isDiasLibreEleccion && (
          <div className="animate-slide-in">
            {/* Mensaje informativo si está en arrancada */}
            {forceRecuperable && (
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 mb-4">
                <div className="flex items-start gap-3">
                  <Info className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-800 dark:text-amber-300">
                      Fechas de arrancada detectadas
                    </p>
                    <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                      En fechas de arrancada (16-18 septiembre) máximo 1 día de libre elección RECUPERABLE.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <label className="label">Tipo de compensación</label>
            <div className="grid grid-cols-1 xs:grid-cols-2 gap-3 sm:gap-4">
              <button
                type="button"
                onClick={() => {
                  if (!forceRecuperable) {
                    setCompensationType('RECUPERABLE')
                    setValue('compensationType', 'RECUPERABLE')
                  }
                }}
                className={`p-3 sm:p-4 rounded-xl border-2 transition-all ${
                  compensationType === 'RECUPERABLE'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                } ${forceRecuperable ? 'cursor-default' : ''}`}
              >
                <div className="text-left">
                  <p className={`font-medium text-sm sm:text-base ${compensationType === 'RECUPERABLE' ? 'text-primary-700 dark:text-primary-300' : 'text-gray-900 dark:text-white'}`}>
                    Recuperable
                  </p>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5 sm:mt-1">
                    El día se recupera trabajando otro día
                  </p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!forceRecuperable) {
                    setCompensationType('DESCUENTO_NOMINA')
                    setValue('compensationType', 'DESCUENTO_NOMINA')
                  }
                }}
                disabled={forceRecuperable}
                className={`p-3 sm:p-4 rounded-xl border-2 transition-all ${
                  compensationType === 'DESCUENTO_NOMINA'
                    ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                    : forceRecuperable
                    ? 'border-gray-200 dark:border-gray-700 opacity-50 cursor-not-allowed'
                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div className="text-left">
                  <p className={`font-medium text-sm sm:text-base ${compensationType === 'DESCUENTO_NOMINA' ? 'text-primary-700 dark:text-primary-300' : 'text-gray-900 dark:text-white'}`}>
                    Descuento en nómina
                  </p>
                  <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5 sm:mt-1">
                    {forceRecuperable
                      ? 'No disponible en arrancada'
                      : 'Se descuenta del sueldo (2 días)'}
                  </p>
                </div>
              </button>
            </div>

            {/* Info adicional según tipo seleccionado */}
            {compensationType === 'RECUPERABLE' && !forceRecuperable && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
                Puedes solicitar 1 o 2 días consecutivos.
              </p>
            )}
            {compensationType === 'DESCUENTO_NOMINA' && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-3">
                Debes seleccionar exactamente 2 días. Pueden ser consecutivos o separados.
              </p>
            )}

            {/* Input oculto para react-hook-form */}
            <input type="hidden" {...register('compensationType')} />
            <input type="hidden" {...register('secondDate')} />
          </div>
        )}

        {/* Notas */}
        <div>
          <label className="label">Notas (opcional)</label>
          <textarea
            {...register('notes')}
            className="input min-h-[100px] resize-none"
            placeholder="Notas adicionales sobre la solicitud..."
          />
        </div>

        {/* Documento Adjunto - Solo para Vacaciones Fiestas Cáseda */}
        {isVacacionesCaseda && (
          <div className="animate-slide-in">
            <div className={`${isDocumentRequired ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800' : 'bg-gray-50 dark:bg-gray-800/20 border-gray-200 dark:border-gray-700'} border rounded-xl p-4 mb-4`}>
              <div className="flex items-start gap-3">
                <Info className={`h-5 w-5 ${isDocumentRequired ? 'text-blue-500' : 'text-gray-400'} flex-shrink-0 mt-0.5`} />
                <div>
                  <p className={`text-sm font-medium ${isDocumentRequired ? 'text-blue-800 dark:text-blue-300' : 'text-gray-700 dark:text-gray-300'}`}>
                    {isDocumentRequired ? 'Documento obligatorio' : 'Documento opcional'}
                  </p>
                  <p className={`text-sm ${isDocumentRequired ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'} mt-1`}>
                    {isDocumentRequired
                      ? 'Para solicitar Vacaciones por Fiestas de Cáseda debes adjuntar un documento que acredite tu empadronamiento en Cáseda. Esta solicitud será revisada por RRHH.'
                      : 'Como administrador, puedes adjuntar opcionalmente el documento de empadronamiento. La solicitud se aprobará automáticamente.'}
                  </p>
                </div>
              </div>
            </div>

            <label className="label flex items-center gap-2">
              <Paperclip className="h-4 w-4 text-gray-400" />
              Documento de empadronamiento {isDocumentRequired && <span className="text-error-500">*</span>}
            </label>

            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileChange}
              className="hidden"
            />

            {attachmentFile ? (
              <div className="flex items-center justify-between p-4 bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded-xl">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-success-100 dark:bg-success-800 rounded-lg">
                    <FileText className="h-5 w-5 text-success-600 dark:text-success-400" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white text-sm">
                      {attachmentFile.name}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {(attachmentFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleClearFile}
                  className="p-1.5 hover:bg-success-100 dark:hover:bg-success-800 rounded-lg transition-colors"
                >
                  <X className="h-4 w-4 text-success-600 dark:text-success-400" />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="w-full p-6 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-xl hover:border-primary-400 dark:hover:border-primary-500 hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors group"
              >
                <div className="flex flex-col items-center gap-2 text-gray-500 dark:text-gray-400">
                  <Upload className="h-8 w-8 group-hover:text-primary-500 transition-colors" />
                  <span className="text-sm font-medium group-hover:text-primary-600 dark:group-hover:text-primary-400">
                    Haz clic para seleccionar archivo
                  </span>
                  <span className="text-xs">PDF, JPG o PNG (máx. 5MB)</span>
                </div>
              </button>
            )}
          </div>
        )}

        {/* Validation Section */}
        <div className="border-t border-gray-200 dark:border-dark-border pt-6">
          <button
            type="button"
            onClick={handleValidate}
            className="btn btn-secondary w-full mb-4"
          >
            Validar Solicitud
          </button>

          {validation && (
            <div
              className={`rounded-xl p-4 animate-slide-in ${
                validation.isValid
                  ? 'bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800'
                  : 'bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800'
              }`}
            >
              <div className="flex items-center gap-2 mb-3">
                {validation.isValid ? (
                  <>
                    <CheckCircle className="h-5 w-5 text-success-600 dark:text-success-400" />
                    <span className="font-semibold text-success-800 dark:text-success-300">
                      Solicitud válida
                    </span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="h-5 w-5 text-error-600 dark:text-error-400" />
                    <span className="font-semibold text-error-800 dark:text-error-300">
                      Solicitud no válida
                    </span>
                  </>
                )}
              </div>
              {validation.errors.length > 0 && (
                <ul className="list-disc list-inside text-sm text-error-700 dark:text-error-300 space-y-1">
                  {validation.errors.map((err, i) => (
                    <li key={i}>{err}</li>
                  ))}
                </ul>
              )}
              {validation.warnings.length > 0 && (
                <ul className="list-disc list-inside text-sm text-warning-700 dark:text-warning-400 space-y-1 mt-2">
                  {validation.warnings.map((warn, i) => (
                    <li key={i}>{warn}</li>
                  ))}
                </ul>
              )}
              {/* Admin Override - solo visible cuando la validación falla */}
              {isAdmin && !validation.isValid && (
                <label className="flex items-center gap-3 mt-4 pt-3 border-t border-error-200 dark:border-error-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={adminOverride}
                    onChange={(e) => setAdminOverride(e.target.checked)}
                    className="w-4 h-4 rounded border-gray-300 text-amber-600 focus:ring-amber-500"
                  />
                  <span className="text-sm font-medium text-amber-700 dark:text-amber-400">
                    Forzar aprobación (saltar validación de reglas)
                  </span>
                </label>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col-reverse xs:flex-row gap-2 sm:gap-4 pt-2">
          <button
            type="button"
            onClick={() => navigate('/requests')}
            className="flex-1 btn btn-secondary py-2.5 sm:py-3 text-sm sm:text-base"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={isLoading || !canSubmit}
            className="flex-1 btn btn-primary py-2.5 sm:py-3 text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
            title={!canSubmit ? 'Debes validar la solicitud primero' : ''}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                Creando...
              </span>
            ) : (
              'Crear Solicitud'
            )}
          </button>
        </div>
        {!canSubmit && (
          <p className="text-xs sm:text-sm text-secondary-600 dark:text-secondary-400 text-center">
            {!validation?.isValid
              ? 'Debes validar la solicitud antes de poder crearla'
              : isDocumentRequired && !attachmentFile
              ? 'Debes adjuntar el documento de empadronamiento'
              : ''}
          </p>
        )}
      </form>
    </div>
  )
}
